<template>
  <div
    :class="['cf', { 'cf--open': isFilterDrawerOpen }]"
    :aria-modal="isFilterDrawerOpen ? 'true' : undefined"
    :role="isFilterDrawerOpen ? 'dialog' : undefined"
    :aria-hidden="!isFilterDrawerOpen"
  >
    <CollectionFilterModule
      v-for="(value, name) in filters"
      :key="name"
      :filter-type="name"
      :filter-options="name === 'discount' ? sortDiscount(value) : value"
      :applied-filters="appliedFilters[name]"
      @input-change="inputChange"
    />
    <div v-if="appliedFiltersCount > 0 && isFilterDrawerOpen" class="cf__bottom">
      <button @click="emit('clearFilter')" class="cf__bottom-clear" aria-label="Clear all filters">
        Clear Filters
      </button>
      <button
        @click="emit('toggleFilterDrawer')"
        class="btn btn--secondary btn--medium cf__bottom-apply"
        aria-label="Apply filters"
      >
        Apply
      </button>
    </div>
  </div>
</template>

<script setup>
  import { toRefs, computed, ref, watch } from 'vue';
  import CollectionFilterModule from './collection-filter-module.vue';

  const props = defineProps({
    isSecondTurn: {
      type: Boolean,
      default: false,
    },

    isFilterDrawerOpen: {
      type: Boolean,
      default: false,
    },

    collectionData: {
      type: Object,
      required: true,
    },

    filteredProducts: {
      type: Array,
      required: true,
    },

    currentFilter: {
      type: String,
      required: true,
    },

    priceFilterRange: {
      type: Array,
      required: true,
    },

    percentageOffFilterRange: {
      type: Array,
      required: true,
    },

    appliedFilters: {
      type: Object,
      required: true,
    },

    appliedFiltersCount: {
      type: Object,
      required: true,
    },
  });

  const { isSecondTurn, collectionData, filteredProducts, currentFilter, priceFilterRange, percentageOffFilterRange } =
    toRefs(props);

  const emit = defineEmits(['updateAppliedFilters,toggleFilterDrawer,clearFilter']);

  const baseFilters = computed(() => {
    const { colors } = collectionData.value;
    const filterType = {
      category: {},
      color: {},
      size: {},
      type: {},
      price: {},
      condition: {},
      discount: {},
    };
    const modfiedProductsData = filteredProducts.value.filter((product) => product.product_type != 'card');
    return modfiedProductsData.reduce((set, product) => {
      // Process Category Filters
      if (product.product_type !== '') {
        set.category[product.product_type] = (set.category[product.product_type] || 0) + 1;
      }

      // Process Color Filters
      if (colors.length) {
        const colorEntries = colors.filter(({ swatchNames }) =>
          product.variants.some(
            ({ available, color }) => available && swatchNames.split(',').includes(color.toLowerCase())
          )
        );

        colorEntries.forEach((color) => {
          const keyName = color.colorName + '/' + color.colorCode;
          set.color[keyName] = (set.color[keyName] || 0) + 1;
        });
      }

      // Process Size Filters
      const sizeSet = new Set(
        product.variants.filter((variant) => variant.available && variant.size).map((variant) => variant.size)
      );
      if (sizeSet.length) {
        sizeSet.forEach((size) => {
          set.size[size] = (set.size[size] || 0) + 1;
        });
      }

      // Process Type Filters
      product.tags.forEach((tag) => {
        if (tag.startsWith('[Category:')) {
          const keyName = tag.slice(11, -1); // Extract the category name
          set.type[keyName] = (set.type[keyName] || 0) + 1;
        }
      });

      // Process Price Filters
      priceFilterRange.value.forEach(({ value: { min, max }, name }) => {
        if (min <= product.price / 100 && product.price / 100 <= max) {
          set.price[name] = (set.price[name] || 0) + 1;
        }
      });

      // Process Condition Filters
      if (isSecondTurn.value) {
        product.options.forEach((option) => {
          if (option.name === 'Condition') {
            option.values.forEach((condition) => {
              set.condition[condition] = (set.condition[condition] || 0) + 1;
            });
          }
        });
      }

      // Process Percentage Off Filters
      const percentageOffSet = new Set();
      product.variants.forEach((variant) => {
        if (variant.compare_at_price > 0) {
          const percentageOff = ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100;
          percentageOffSet.add(percentageOff);
        }
      });

      percentageOffSet.forEach((percentageOff) => {
        percentageOffFilterRange.value.forEach(({ value: { min, max }, name }) => {
          if (min <= percentageOff && percentageOff <= max) {
            set.discount[name] = (set.discount[name] || 0) + 1;
          }
        });
      });

      return set;
    }, filterType);
  });

  const cacheFilters = ref({ ...baseFilters.value });

  const filters = computed(() => {
    const base = baseFilters.value;
    const cache = cacheFilters.value;

    cacheFilters.value = base;

    switch (currentFilter.value) {
      case 'category':
        cacheFilters.value.category = Object.values(cacheFilters.value.category).length
          ? cache.category
          : base.category;
        return { ...base, category: cacheFilters.value.category };
      case 'color':
        cacheFilters.value.color = Object.values(cacheFilters.value.color).length ? cache.color : base.color;
        return { ...base, color: cacheFilters.value.color };
      case 'size':
        cacheFilters.value.size = Object.values(cacheFilters.value.size).length ? cache.size : base.size;
        return { ...base, size: cacheFilters.value.size };
      case 'type':
        cacheFilters.value.type = Object.values(cacheFilters.value.type).length ? cache.type : base.type;
        return { ...base, type: cacheFilters.value.type };
      case 'price':
        cacheFilters.value.price = Object.values(cacheFilters.value.price).length ? cache.price : base.price;
        return { ...base, price: cacheFilters.value.price };
      case 'condition':
        cacheFilters.value.condition = Object.values(cacheFilters.value.condition).length
          ? cache.condition
          : base.condition;
        return { ...base, condition: cacheFilters.value.condition };
      case 'discount':
        cacheFilters.value.discount = Object.values(cacheFilters.value.discount).length
          ? cache.discount
          : base.discount;
        return { ...base, discount: cacheFilters.value.discount };
      default:
        return base;
    }
  });

  const sortDiscount = (value) => {
    const sortedData = Object.fromEntries(
      Object.entries(value).sort(([range1], [range2]) => {
        const range1Min = parseInt(range1.split('%')[0]);
        const range2Min = parseInt(range2.split('%')[0]);
        return range1Min - range2Min;
      })
    );
    return sortedData;
  };

  const inputChange = (filterKey, options) => {
    emit('updateAppliedFilters', filterKey, options);
  };

  watch(baseFilters, () => {
    if (!currentFilter.value) {
      cacheFilters.value = { ...baseFilters.value };
    }
  });

  defineExpose(filters.value);
</script>
